/**
 * Scoring helpers for the Security Summary Assessment Results charts.
 *
 * Mirrors backend/agent_service/utils/scoring.py's rating bands and the
 * risk scale documented there: completeness is 0-100 (higher is better),
 * system_risk_score lands on a roughly 0-10 scale from averaged
 * probability (1.5-9.5) / severity (1-8.5) values (higher is WORSE).
 */

export const RISK_SCALE_MAX = 10;
const RISK_LOW_THRESHOLD = 3.3;
const RISK_MODERATE_THRESHOLD = 6.6;

export type CompletenessBucket =
  | 'status-good'
  | 'status-medium'
  | 'status-poor';

export interface CompletenessRating {
  rating: string;
  bucket: CompletenessBucket;
  colorVar: string;
}

/** Maps a completeness score (0-100) to a rating label + 3-tier color bucket. */
export function getCompletenessRating(score: number): CompletenessRating {
  let rating: string;
  if (score <= 40) rating = 'CRITICAL';
  else if (score <= 50) rating = 'POOR';
  else if (score <= 70) rating = 'AVERAGE';
  else if (score <= 85) rating = 'GOOD';
  else rating = 'EXCELLENT';

  let bucket: CompletenessBucket;
  let colorVar: string;
  if (score <= 50) {
    bucket = 'status-poor';
    colorVar = 'var(--kone-error)';
  } else if (score <= 70) {
    bucket = 'status-medium';
    colorVar = 'var(--kone-warning)';
  } else {
    bucket = 'status-good';
    colorVar = 'var(--kone-success)';
  }

  return { rating, bucket, colorVar };
}

export interface RiskLevel {
  label: string;
  colorVar: string;
}

/** Maps a risk score (0-10, higher is worse) to a category label + color. */
export function getRiskLevel(score: number): RiskLevel {
  if (score <= RISK_LOW_THRESHOLD) {
    return { label: 'Low Risk', colorVar: 'var(--kone-success)' };
  }
  if (score <= RISK_MODERATE_THRESHOLD) {
    return { label: 'Moderate Risk', colorVar: 'var(--kone-warning)' };
  }
  return { label: 'High Risk', colorVar: 'var(--kone-error)' };
}

/** True when a report has actual evaluation results attached (not just a pending request row). */
export function hasEvaluationData(
  report:
    | {
        report_url?: string | null;
        url?: string | null;
        system_risk_score?: number | null;
        completeness_score?: number | null;
        content_id?: number | null;
      }
    | null
    | undefined,
): boolean {
  if (!report) return false;
  const url = report.report_url ?? report.url;
  return Boolean(
    url &&
      report.system_risk_score !== null &&
      report.system_risk_score !== undefined &&
      report.completeness_score !== null &&
      report.completeness_score !== undefined,
  );
}
